/**
 * Phase 4 — Payments (G.19). Client for the manager payment-link endpoints.
 */
export interface TicketPaymentDto {
  TicketPayment_guid: string;
  TicketPayment_AmountCents: number;
  TicketPayment_Note: string | null;
  TicketPayment_Url: string | null;
  TicketPayment_Status: string;
  /** How the money arrived — a Stripe method, or one staff picked when recording it by hand. */
  TicketPayment_Method: string;
  /** When the money was actually received (null for a link nobody has paid yet). */
  TicketPayment_ReceivedAt: string | null;
  /** Cheque number, Zelle confirmation, Clio payment id. */
  TicketPayment_Reference: string | null;
  TicketPayment_CreatedByName: string | null;
  TicketPayment_CreatedAt: string;
  /** 'fee' (the firm's income) or 'pass_through' (the client's money owed onward to a court). */
  TicketPayment_Kind: string;
  /** On a pass-through only: how much of the amount is owed onward to the court, in cents. */
  TicketPayment_PassThroughBaseCents: number | null;
  /** Which fee this is — 'flat', 'appeal' or 'jury' — when staff said so; null otherwise. */
  TicketPayment_FeeKind: FeeKind | null;
}

/**
 * Ways staff can record a payment the firm already has (Lloyd, Jul 29 2026). Mirrors the server's
 * list — a value not in it is rejected.
 */
export const MANUAL_PAYMENT_METHODS = [
  'cash',
  'zelle',
  'check',
  'money_order',
  'card_in_office',
  'clio',
  'other',
] as const;

export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  stripe_link: 'Stripe payment link',
  stripe_checkout: 'Stripe checkout',
  cash: 'Cash',
  zelle: 'Zelle',
  check: 'Check',
  money_order: 'Money order',
  card_in_office: 'Card (in office)',
  clio: 'Clio (already paid)',
  other: 'Other',
};

export function paymentMethodLabel(method: string | null | undefined): string {
  if (!method) return 'Unknown';
  return PAYMENT_METHOD_LABELS[method] ?? method;
}

/**
 * How a payment row reads on screen, and what colour it carries.
 *
 * The case panel and the payment search each had their own ternary over the same statuses, so
 * adding `cancelled` would have shown a dead link as "link created" on the search — exactly the
 * wrong thing to tell someone checking whether a link is still live. One definition, used by both.
 */
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  paid: 'paid',
  voided: 'voided',
  cancelled: 'cancelled',
  link_created: 'link created',
};

export function paymentStatusLabel(status: string | null | undefined): string {
  if (!status) return 'unknown';
  return PAYMENT_STATUS_LABELS[status] ?? status;
}

/** True when the row no longer represents money that can arrive. */
export function isDeadPayment(status: string | null | undefined): boolean {
  return status === 'voided' || status === 'cancelled';
}

export function paymentStatusColor(
  status: string | null | undefined,
): 'error' | 'success' | 'default' {
  if (isDeadPayment(status)) return 'error';
  return status === 'paid' ? 'success' : 'default';
}

export function isStripePayment(method: string | null | undefined): boolean {
  return method === 'stripe_link' || method === 'stripe_checkout';
}

/** Input for recording a payment received outside Stripe. */
export interface RecordManualPaymentInput {
  amountCents: number;
  method: ManualPaymentMethod;
  /** ISO date. Defaults to now on the server when omitted. */
  receivedAt?: string;
  reference?: string;
  note?: string;
  /** Which fee this money was for, when staff said so. A paid appeal fee fills PD on the chart. */
  feeKind?: FeeKind | null;
}

// G.17/G.18 — permissioned per-citation amount + pre-paid third-party flag.
export interface PaymentAmountDto {
  Ticket_PaymentAmountCents: number | null;
  Ticket_PrepaidThirdParty: boolean;
}

/** Period totals for one collection channel. */
export interface RevenueChannelDto {
  todayCents: number;
  weekCents: number;
  monthCents: number;
  yearCents: number;
  allTimeCents: number;
  paidLinkCount: number;
  /**
   * All-time money collected on a court's behalf and owed onward — excluded from every figure
   * above. Shown so the obligation is visible rather than silently missing.
   */
  passedThroughToCourtsCents: number;
}

// G.20 — Lloyd-only revenue snapshot. Stripe and non-Stripe money are reported separately so the
// Stripe figure stays reconcilable against the Stripe dashboard; collectedTotal is the two combined.
export interface RevenueSummaryDto {
  currency: 'usd';
  collectedViaStripe: RevenueChannelDto;
  collectedOutsideStripe: RevenueChannelDto;
  collectedTotal: RevenueChannelDto;
  onRecord: {
    citationAmountTotalCents: number;
    citationCount: number;
    prepaidThirdPartyTotalCents: number;
    prepaidThirdPartyCount: number;
  };
}

/** The pass-through quote for a case: the court's fine, the firm's add-on, and the total to bill. */
export interface PassThroughQuoteDto {
  courtFineCents: number;
  addOnCents: number;
  totalCents: number;
  percent: number;
  rounding: string;
}

export interface CourtFineDto {
  Ticket_CourtFineCents: number | null;
  /** Null when no fine is on record — unknown stays distinguishable from zero. */
  passThroughQuote: PassThroughQuoteDto | null;
}

/**
 * Suggested fees for a case — the firm rate card resolved against this client's payment history
 * (Lloyd, Jul 20: prefilled but editable). Advisory only; staff still confirm the amount.
 */
export type FeeKind = 'flat' | 'appeal' | 'jury';

export interface FeeSuggestionDto {
  kind: FeeKind;
  label: string;
  suggestedCents: number | null;
  /** The arithmetic behind the number — shown to staff so the credit is never silent. */
  basis: string;
  /** true = no defensible default; the number (if any) must be confirmed or replaced. */
  requiresManualEntry: boolean;
}

export interface FeeSuggestionsDto {
  source: string | null;
  paymentsCountedCents: number | null;
  paymentsNote: string;
  suggestions: FeeSuggestionDto[];
  /** true = jury trial only · false = can appeal to county · null = unknown. */
  isCourtOfRecord: boolean | null;
}

/**
 * A payment match from the cross-case search (Hannah, Sep 18 2026), with enough case context to
 * open the right case directly.
 */
export interface PaymentSearchResultDto extends TicketPaymentDto {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_MatterNumber: string | null;
  Ticket_Citation: string;
  clientName: string | null;
  TicketPayment_StripePaymentLinkId: string | null;
}

/** Thrown when the caller lacks the payments permission (HTTP 403). Lets UI hide payment sections. */
export class PaymentsForbiddenError extends Error {}

/**
 * Thrown when a payment link is blocked because the case is Off the Record (HTTP 409). OTR collects
 * the client's money directly, so a link is only allowed for a jury/appeal escalation — the UI catches
 * this to prompt staff to confirm the escalation before retrying with isEscalationFee.
 */
export class OtrPaymentBlockedError extends Error {}

class PaymentApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  /**
   * Create a Stripe payment link for a case (amount in cents, optional internal note).
   * `isEscalationFee` asserts this OTR case is escalating to a jury trial / county appeal — the only
   * time an OTR client is billed directly. A standard OTR case throws OtrPaymentBlockedError (409).
   */
  /**
   * The court's fine on a case, with the pass-through quote derived from it.
   *
   * The quote comes from the server so the 10% rule and its rounding live in exactly one place —
   * the alternative is a second copy in the browser that drifts the first time Lloyd changes it.
   */
  static async getCourtFine(ticketGuid: string): Promise<CourtFineDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/court-fine`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to read the court fine');
    return response.json();
  }

  static async setCourtFine(
    ticketGuid: string,
    courtFineCents: number | null,
  ): Promise<CourtFineDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/court-fine`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ courtFineCents }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to save the court fine');
    }
    return response.json();
  }

  static async createPaymentLink(
    ticketGuid: string,
    amountCents: number,
    note?: string,
    isEscalationFee?: boolean,
    /**
     * Pass a pass-through when the money is the client's own, bound for a court — the firm's
     * revenue then counts only the add-on, and the OTR double-charge guard does not apply because
     * this is not the firm's fee. `passThroughBaseCents` is required with it.
     */
    passThrough?: { passThroughBaseCents: number },
    /** Which fee the link is for, when it was built from a fee suggestion. */
    feeKind?: FeeKind | null,
  ): Promise<TicketPaymentDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment-link`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amountCents,
          note: note?.trim() || undefined,
          isEscalationFee: isEscalationFee || undefined,
          kind: passThrough ? 'pass_through' : undefined,
          passThroughBaseCents: passThrough?.passThroughBaseCents,
          feeKind: passThrough ? undefined : feeKind || undefined,
        }),
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: 'Failed to create payment link' }));
      const message =
        err.details || err.error || err.message || 'Failed to create payment link';
      if (response.status === 409) throw new OtrPaymentBlockedError(message);
      throw new Error(message);
    }
    return response.json();
  }

  /** G.17/G.18 — read the permissioned amount + pre-paid flag for a case (403 if not permitted). */
  static async getPaymentAmount(ticketGuid: string): Promise<PaymentAmountDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment-amount`,
      { method: 'GET', credentials: 'include' },
    );
    if (response.status === 403) throw new PaymentsForbiddenError('Not permitted');
    if (!response.ok) throw new Error('Failed to load payment amount');
    return response.json();
  }

  /** G.17/G.18 — set the permissioned amount + pre-paid flag (403 if not permitted). */
  static async setPaymentAmount(
    ticketGuid: string,
    amountCents: number | null,
    prepaidThirdParty: boolean,
  ): Promise<PaymentAmountDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment-amount`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amountCents, prepaidThirdParty }),
      },
    );
    if (response.status === 403) throw new PaymentsForbiddenError('Not permitted');
    if (!response.ok) throw new Error('Failed to save payment amount');
    return response.json();
  }

  /** Suggested fees for a case, resolved against its referral source, court and payments (403 if not permitted). */
  static async getFeeSuggestions(ticketGuid: string): Promise<FeeSuggestionsDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/fee-suggestions`,
      { method: 'GET', credentials: 'include' },
    );
    if (response.status === 403) throw new PaymentsForbiddenError('Not permitted');
    if (!response.ok) throw new Error('Failed to load fee suggestions');
    return response.json();
  }

  /** G.20 — revenue snapshot for the Lloyd-only dashboard (403 if not permitted). */
  static async getRevenueSummary(): Promise<RevenueSummaryDto> {
    const response = await fetch(`${PaymentApi.host()}/api/v1/manager/revenue-summary`, {
      method: 'GET',
      credentials: 'include',
    });
    if (response.status === 403) throw new PaymentsForbiddenError('Not permitted');
    if (!response.ok) throw new Error('Failed to load revenue summary');
    return response.json();
  }

  /**
   * Cross-case payment search (Hannah, Sep 18 2026) — search a Stripe payment link id, a staff
   * reference, an amount, or a client/case, and get back which case each match belongs to.
   * 403 if not permitted.
   */
  static async searchPayments(query: string): Promise<PaymentSearchResultDto[]> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/payments/search?q=${encodeURIComponent(query)}`,
      { method: 'GET', credentials: 'include' },
    );
    if (response.status === 403) throw new PaymentsForbiddenError('Not permitted');
    if (!response.ok) throw new Error('Failed to search payments');
    const data = await response.json();
    return data.results ?? [];
  }

  /**
   * Record a payment the firm received outside Stripe — cash, Zelle, cheque, or one already taken
   * in Clio. Saved as paid immediately, so it counts toward the case total straight away.
   */
  static async recordManualPayment(
    ticketGuid: string,
    input: RecordManualPaymentInput,
  ): Promise<TicketPaymentDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/manual-payment`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amountCents: input.amountCents,
          method: input.method,
          receivedAt: input.receivedAt || undefined,
          reference: input.reference?.trim() || undefined,
          note: input.note?.trim() || undefined,
          feeKind: input.feeKind || undefined,
        }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to record payment' }));
      throw new Error(
        err.details || err.message || err.error || 'Failed to record payment',
      );
    }
    return response.json();
  }

  /**
   * Void a manually-recorded payment entered in error. The row is kept for audit but drops out of
   * every total. Stripe payments are refused (409) — those are corrected by refunding in Stripe.
   */
  static async voidPayment(
    ticketGuid: string,
    paymentGuid: string,
    reason?: string,
  ): Promise<TicketPaymentDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment/${paymentGuid}/void`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: reason?.trim() || undefined }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to void payment' }));
      throw new Error(err.details || err.message || err.error || 'Failed to void payment');
    }
    return response.json();
  }

  /**
   * Cancel an unpaid payment link so a replaced link cannot still be used.
   *
   * Stripe has no delete for a Payment Link — the server deactivates it there and keeps our row as
   * `cancelled` for audit. A link already paid is refused (409); that is a refund in Stripe.
   */
  static async cancelPaymentLink(
    ticketGuid: string,
    paymentGuid: string,
    reason?: string,
  ): Promise<TicketPaymentDto> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment/${paymentGuid}/cancel-link`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: reason?.trim() || undefined }),
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: 'Failed to cancel payment link' }));
      throw new Error(
        err.details || err.message || err.error || 'Failed to cancel payment link',
      );
    }
    return response.json();
  }

  /** List payment links already generated for a case. */
  static async listPaymentLinks(ticketGuid: string): Promise<TicketPaymentDto[]> {
    const response = await fetch(
      `${PaymentApi.host()}/api/v1/manager/ticket/${ticketGuid}/payment-links`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: 'Failed to load payment links' }));
      throw new Error(err.error || err.message || 'Failed to load payment links');
    }
    return response.json();
  }
}

export default PaymentApi;
